import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    ArrowLeft,
    FileText,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Scale,
    ShieldCheck,
    CreditCard,
    RefreshCw,
    Target,
} from 'lucide-react';

export default function TermsPage() {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col">
            {/* Header */}
            <header className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg">
                            <ArrowLeft className="w-5 h-5" />
                        </Link>
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary-600" />
                            <h1 className="text-xl font-bold">{t('terms.title', 'Điều khoản Sử dụng')}</h1>
                        </div>
                    </div>
                    <Link to="/login" className="btn-primary text-sm">
                        {t('auth.login')}
                    </Link>
                </div>
            </header>

            {/* Content */}
            <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
                {/* Hero */}
                <section className="text-center py-8">
                    <div className="w-16 h-16 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-primary-600" />
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                        {t('terms.hero.title', 'Điều khoản Sử dụng Dịch vụ')}
                    </h2>
                    <p className="text-neutral-600 dark:text-neutral-400 max-w-xl mx-auto mb-4">
                        {t('terms.hero.subtitle', 'Vui lòng đọc kỹ các điều khoản này trước khi sử dụng VisionBoard.')}
                    </p>
                    <p className="text-sm text-neutral-500">
                        {t('terms.hero.lastUpdated', 'Có hiệu lực từ: 11 tháng 12, 2024')}
                    </p>
                </section>

                {/* Quick Summary */}
                <section className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700 mb-8">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <Scale className="w-5 h-5 text-primary-600" />
                        {t('terms.summary.title', 'Tóm tắt quan trọng')}
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-sm">{t('terms.summary.can1', 'Bạn có thể')}</p>
                                <p className="text-sm text-neutral-600 dark:text-neutral-400">{t('terms.summary.can1Desc', 'Sử dụng miễn phí cho mục đích cá nhân và team')}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-sm">{t('terms.summary.can2', 'Bạn có thể')}</p>
                                <p className="text-sm text-neutral-600 dark:text-neutral-400">{t('terms.summary.can2Desc', 'Xuất dữ liệu của mình bất kỳ lúc nào')}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <XCircle className="w-5 h-5 text-danger-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-sm">{t('terms.summary.cant1', 'Không được')}</p>
                                <p className="text-sm text-neutral-600 dark:text-neutral-400">{t('terms.summary.cant1Desc', 'Sử dụng dịch vụ cho mục đích bất hợp pháp')}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <XCircle className="w-5 h-5 text-danger-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-sm">{t('terms.summary.cant2', 'Không được')}</p>
                                <p className="text-sm text-neutral-600 dark:text-neutral-400">{t('terms.summary.cant2Desc', 'Cố gắng xâm nhập hoặc phá hoại hệ thống')}</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Sections */}
                <div className="space-y-6">
                    {/* 1. Introduction */}
                    <section className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                        <h3 className="text-lg font-semibold mb-3">1. {t('terms.sections.intro.title', 'Giới thiệu')}</h3>
                        <div className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed space-y-3">
                            <p>{t('terms.sections.intro.p1', 'VisionBoard ("Chúng tôi", "Dịch vụ") cung cấp công cụ quản lý mục tiêu (OKR) và công việc (Kanban) giúp cá nhân và team theo dõi tiến độ và đạt được mục tiêu.')}</p>
                            <p>{t('terms.sections.intro.p2', 'Bằng việc truy cập hoặc sử dụng dịch vụ, bạn đồng ý tuân thủ các điều khoản này. Nếu không đồng ý, vui lòng không sử dụng dịch vụ.')}</p>
                        </div>
                    </section>

                    {/* 2. Account */}
                    <section className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-primary-600" />
                            2. {t('terms.sections.account.title', 'Tài khoản Người dùng')}
                        </h3>
                        <div className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed space-y-3">
                            <p>{t('terms.sections.account.p1', 'Để sử dụng dịch vụ, bạn cần:')}</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>{t('terms.sections.account.item1', 'Cung cấp thông tin chính xác khi đăng ký')}</li>
                                <li>{t('terms.sections.account.item2', 'Bảo mật thông tin đăng nhập của bạn')}</li>
                                <li>{t('terms.sections.account.item3', 'Thông báo ngay nếu phát hiện truy cập trái phép')}</li>
                                <li>{t('terms.sections.account.item4', 'Đủ 13 tuổi trở lên để sử dụng dịch vụ')}</li>
                            </ul>
                            <p>{t('terms.sections.account.p2', 'Bạn chịu trách nhiệm cho mọi hoạt động diễn ra dưới tài khoản của mình.')}</p>
                        </div>
                    </section>

                    {/* 3. Acceptable Use */}
                    <section className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-warning-600" />
                            3. {t('terms.sections.acceptable.title', 'Quy tắc Sử dụng')}
                        </h3>
                        <div className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed space-y-3">
                            <p>{t('terms.sections.acceptable.p1', 'Khi sử dụng VisionBoard, bạn đồng ý KHÔNG:')}</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>{t('terms.sections.acceptable.item1', 'Vi phạm pháp luật hoặc quy định hiện hành')}</li>
                                <li>{t('terms.sections.acceptable.item2', 'Gửi spam, quấy rối hoặc nội dung xúc phạm')}</li>
                                <li>{t('terms.sections.acceptable.item3', 'Cố gắng truy cập trái phép vào hệ thống')}</li>
                                <li>{t('terms.sections.acceptable.item4', 'Sao chép, phân phối hoặc tạo sản phẩm phái sinh')}</li>
                                <li>{t('terms.sections.acceptable.item5', 'Sử dụng bot hoặc script tự động không được phép')}</li>
                            </ul>
                            <p className="text-warning-600 dark:text-warning-400">{t('terms.sections.acceptable.warning', 'Vi phạm có thể dẫn đến đình chỉ hoặc xóa tài khoản.')}</p>
                        </div>
                    </section>

                    {/* 4. Payment */}
                    <section className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-success-600" />
                            4. {t('terms.sections.payment.title', 'Thanh toán & Gói dịch vụ')}
                        </h3>
                        <div className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed space-y-3">
                            <p>{t('terms.sections.payment.p1', 'VisionBoard cung cấp gói miễn phí với đầy đủ tính năng cơ bản. Các gói trả phí (nếu có) sẽ:')}</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>{t('terms.sections.payment.item1', 'Được thanh toán theo tháng hoặc năm')}</li>
                                <li>{t('terms.sections.payment.item2', 'Tự động gia hạn trừ khi bạn hủy trước ngày gia hạn')}</li>
                                <li>{t('terms.sections.payment.item3', 'Hoàn tiền trong 14 ngày đầu nếu không hài lòng')}</li>
                            </ul>
                        </div>
                    </section>

                    {/* 5. Intellectual Property */}
                    <section className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                        <h3 className="text-lg font-semibold mb-3">5. {t('terms.sections.ip.title', 'Quyền Sở hữu Trí tuệ')}</h3>
                        <div className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed space-y-3">
                            <p>{t('terms.sections.ip.p1', 'VisionBoard và tất cả nội dung, tính năng, giao diện thuộc sở hữu của chúng tôi và được bảo hộ bởi luật sở hữu trí tuệ.')}</p>
                            <p>{t('terms.sections.ip.p2', 'Bạn sở hữu dữ liệu bạn tạo ra (mục tiêu, công việc, board). Chúng tôi không claim quyền sở hữu đối với nội dung của bạn.')}</p>
                        </div>
                    </section>

                    {/* 6. Termination */}
                    <section className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                            <RefreshCw className="w-5 h-5 text-secondary-600" />
                            6. {t('terms.sections.termination.title', 'Chấm dứt Dịch vụ')}
                        </h3>
                        <div className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed space-y-3">
                            <p>{t('terms.sections.termination.p1', 'Bạn có thể hủy tài khoản bất kỳ lúc nào trong Cài đặt.')}</p>
                            <p>{t('terms.sections.termination.p2', 'Chúng tôi có thể đình chỉ hoặc chấm dứt tài khoản nếu bạn vi phạm điều khoản, với thông báo trước khi có thể.')}</p>
                        </div>
                    </section>

                    {/* 7. Disclaimer */}
                    <section className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                        <h3 className="text-lg font-semibold mb-3">7. {t('terms.sections.disclaimer.title', 'Giới hạn Trách nhiệm')}</h3>
                        <div className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed space-y-3">
                            <p>{t('terms.sections.disclaimer.p1', 'Dịch vụ được cung cấp "nguyên trạng" (as is). Chúng tôi không đảm bảo dịch vụ hoạt động liên tục hoặc không có lỗi.')}</p>
                            <p>{t('terms.sections.disclaimer.p2', 'Trong phạm vi pháp luật cho phép, chúng tôi không chịu trách nhiệm cho các thiệt hại gián tiếp, ngẫu nhiên, hoặc hậu quả.')}</p>
                        </div>
                    </section>

                    {/* 8. Changes */}
                    <section className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                        <h3 className="text-lg font-semibold mb-3">8. {t('terms.sections.changes.title', 'Thay đổi Điều khoản')}</h3>
                        <div className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed">
                            <p>{t('terms.sections.changes.p1', 'Chúng tôi có thể cập nhật điều khoản này theo thời gian. Các thay đổi quan trọng sẽ được thông báo qua email hoặc thông báo trong ứng dụng. Việc tiếp tục sử dụng dịch vụ sau khi thay đổi có hiệu lực đồng nghĩa với việc bạn chấp nhận điều khoản mới.')}</p>
                        </div>
                    </section>
                </div>

                {/* Contact */}
                <section className="mt-8 text-center py-8 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-2xl text-white">
                    <h3 className="text-xl font-bold mb-2">
                        {t('terms.contact.title', 'Có câu hỏi về điều khoản?')}
                    </h3>
                    <p className="text-white/80 mb-4">
                        {t('terms.contact.desc', 'Liên hệ với chúng tôi')}
                    </p>
                    <a href="mailto:support@1vision.work" className="text-white hover:underline font-medium">
                        support@1vision.work
                    </a>
                </section>
            </main>

            {/* Footer */}
            <footer className="py-8 px-4 bg-neutral-100 dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700 mt-auto">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                            <Target className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-neutral-900 dark:text-white">VisionBoard</span>
                    </Link>
                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                        <Link to="/privacy" className="hover:text-primary-600">Bảo mật</Link>
                        <Link to="/contact" className="hover:text-primary-600">Liên hệ</Link>
                        <Link to="/help" className="hover:text-primary-600">Hỗ trợ</Link>
                    </div>
                    <p className="text-sm text-neutral-500">© 2025 VisionBoard</p>
                </div>
            </footer>
        </div>
    );
}
